import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        destructive: 'border-transparent bg-destructive/10 text-destructive',
        outline: 'text-foreground border-border',
        success: 'border-transparent bg-emerald-50 text-emerald-700',
        warning: 'border-transparent bg-amber-50 text-amber-700',
        info: 'border-transparent bg-sky-50 text-sky-700',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

// Detects raw status-like values (e.g. "paid", "in_progress", "waiting_customer")
// so we can format them as "Paid", "In Progress", "Waiting Customer" automatically.
const RAW_STATUS_RE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

function formatStatusLabel(value: string): string {
  return value
    .split('_')
    .map((part) => (part.length === 0 ? part : part[0].toUpperCase() + part.slice(1)))
    .join(' ');
}

function formatBadgeChildren(children: React.ReactNode): React.ReactNode {
  if (typeof children === 'string' && RAW_STATUS_RE.test(children)) {
    return formatStatusLabel(children);
  }
  return children;
}

function Badge({ className, variant, children, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {formatBadgeChildren(children)}
    </div>
  );
}

export { Badge, badgeVariants, formatStatusLabel };
